import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { pillarStateKey } from '../lib/livingAvatarStates';
import { scenerySrc } from '../lib/worldScenes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AvatarContext = createContext(null);
export const useAvatar = () => useContext(AvatarContext);

// Append the auth token to a backend image URL (an <img> can't send headers).
const withAuth = (url) => {
  if (!url) return url;
  if (!url.startsWith('/api/')) return url; // static asset
  const token = localStorage.getItem('hpme_token') || '';
  const sep = url.includes('?') ? '&' : '?';
  return `${BACKEND_URL}${url}${sep}auth=${token}`;
};

/**
 * AvatarProvider — single source of truth for the Living Visual Layer's images.
 * Loads the user's generated avatar manifest once; exposes `status` and a
 * `sceneSrc(pillar, score)` that returns the user's own scene when ready, or the
 * shared fallback set otherwise. Composite is fetched per-call by LivingHome.
 */
export const AvatarProvider = ({ children }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState('none'); // none | generating | ready | failed
  const [slots, setSlots] = useState({});

  const refresh = useCallback(async () => {
    try {
      const res = await api.getAvatarStates();
      setStatus(res.data?.status || 'none');
      setSlots(res.data?.slots || {});
    } catch (e) {
      setStatus('none');
      setSlots({});
    }
  }, []);

  // Re-fetch whenever the auth token changes (e.g. first login) so the avatar
  // appears immediately without needing a full app reload. On logout, reset.
  useEffect(() => {
    if (token) {
      refresh();
    } else {
      setStatus('none');
      setSlots({});
    }
    const onChanged = () => refresh();
    window.addEventListener('avatar:changed', onChanged);
    return () => window.removeEventListener('avatar:changed', onChanged);
  }, [token, refresh]);

  const ready = status === 'ready';

  const sceneSrc = useCallback(
    (pillar, score) => {
      const slot = `${pillar}_${pillarStateKey(score)}`;
      if (ready && slots[slot]) return withAuth(slots[slot]);
      // No personal avatar yet → show the clean, character-free world SCENERY,
      // never the old pre-Gemini placeholder character.
      return scenerySrc(pillar, score);
    },
    [ready, slots]
  );

  const value = { status, ready, refresh, sceneSrc };
  return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>;
};

export default AvatarProvider;
